/**
 * OGC API Features client for sb-platform.
 * Fetches boundaries, searches by property filter, reverse-geocodes via bbox.
 */

import { AggregationLevel, OgcFeatureCollection, BoundarySearchResult } from '@/types/odin';
import { mapFeatureToSearchResult, getCollectionId } from './ogc-mappers';

const OGC_BASE = '/oapi';

// Cached boundary lists for instant client-side search (small datasets)
const boundaryCache: Partial<Record<AggregationLevel, BoundarySearchResult[]>> = {};
const cachePromises: Partial<Record<AggregationLevel, Promise<BoundarySearchResult[]>>> = {};

/** Fetch all boundaries for a level (cached). Gemeente=342, Provincie=12 — fine to load all. */
async function getAllBoundaries(level: AggregationLevel): Promise<BoundarySearchResult[]> {
  if (boundaryCache[level]) return boundaryCache[level]!;
  if (cachePromises[level]) return cachePromises[level]!;

  const collectionId = getCollectionId(level);
  // PC4 has ~4000 entries — too many to preload, use server-side filtering
  const maxItems = level === 'pc4' ? 0 : 500;
  if (maxItems === 0) return [];

  const promise = (async () => {
    try {
      const res = await fetch(
        `${OGC_BASE}/collections/${collectionId}/items?f=json&limit=${maxItems}&sortby=statnaam`,
      );
      if (!res.ok) return [];
      const data: OgcFeatureCollection = await res.json();
      const results = data.features
        .map((f) => mapFeatureToSearchResult(f, level))
        .filter((r): r is BoundarySearchResult => r !== null);
      boundaryCache[level] = results;
      return results;
    } catch {
      return [];
    }
  })();

  cachePromises[level] = promise;
  return promise;
}

/**
 * Search boundaries by name prefix. Uses client-side filtering for gemeente/provincie
 * (small datasets cached on first use) and server-side range filter for PC4.
 */
export async function searchBoundaries(
  query: string,
  level: AggregationLevel,
  limit = 10,
): Promise<BoundarySearchResult[]> {
  if (level === 'pc4') {
    // PC4: server-side range filter (too many for client cache)
    const numQuery = parseInt(query, 10);
    if (isNaN(numQuery)) return [];
    const padded = query.padEnd(4, '0');
    const upperPadded = query.padEnd(4, '9');
    const lower = parseInt(padded, 10);
    const upper = parseInt(upperPadded, 10);
    const collectionId = getCollectionId(level);
    const url = `${OGC_BASE}/collections/${collectionId}/items?f=json&limit=${limit}&postcode=${lower}..${upper}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const data: OgcFeatureCollection = await res.json();
      return data.features
        .map((f) => mapFeatureToSearchResult(f, level))
        .filter((r): r is BoundarySearchResult => r !== null);
    } catch {
      return [];
    }
  }

  // Gemeente/Provincie: client-side prefix filter on cached list
  const all = await getAllBoundaries(level);
  const q = query.toLowerCase();
  return all
    .filter((r) => r.name.toLowerCase().includes(q))
    .slice(0, limit);
}

/**
 * Reverse-geocode coordinates to PC4 via bbox spatial query.
 * Uses a tight bbox around the point to find the intersecting PC4 area.
 */
export async function reverseGeocodePC4(
  lng: number,
  lat: number,
): Promise<{ postcode: string; feature: GeoJSON.Feature } | null> {
  // ~100m bbox around the point
  const delta = 0.001;
  const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;

  const url = `${OGC_BASE}/collections/boundaries-pc4/items?f=json&limit=1&bbox=${bbox}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const data: OgcFeatureCollection = await res.json();
    if (data.features.length === 0) return null;

    const feature = data.features[0];
    const postcode = String(feature.properties?.postcode ?? '');
    return postcode ? { postcode, feature } : null;
  } catch {
    return null;
  }
}

/**
 * Reverse-geocode coordinates to gemeente via bbox spatial query.
 */
export async function reverseGeocodeGemeente(
  lng: number,
  lat: number,
): Promise<{ name: string; code: string } | null> {
  const delta = 0.001;
  const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;

  const url = `${OGC_BASE}/collections/boundaries-gemeente/items?f=json&limit=1&bbox=${bbox}`;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const data: OgcFeatureCollection = await res.json();
    if (data.features.length === 0) return null;

    const feature = data.features[0];
    return {
      name: feature.properties?.statnaam ?? '',
      code: feature.properties?.statcode ?? '',
    };
  } catch {
    return null;
  }
}

/**
 * Fetch a single boundary feature by code (statcode or postcode).
 */
export async function fetchBoundaryByCode(
  level: AggregationLevel,
  code: string,
): Promise<GeoJSON.Feature | null> {
  const collectionId = getCollectionId(level);

  let filterParam: string;
  if (level === 'pc4') {
    filterParam = `postcode=${code}`;
  } else {
    filterParam = `statcode=${code}`;
  }

  const url = `${OGC_BASE}/collections/${collectionId}/items?f=json&limit=1&${filterParam}`;

  try {
    // Validate: PC4 codes must be numeric
    if (level === 'pc4' && !/^\d+$/.test(code)) return null;

    const res = await fetch(url);
    if (!res.ok) return null;

    const data: OgcFeatureCollection = await res.json();
    return data.features[0] ?? null;
  } catch {
    return null;
  }
}
